import type {
  Metafield,
  Product,
  ProductVariant,
  ProductVariantConnection,
  ProductVariantEdge,
} from "@shopify/hydrogen-react/storefront-api-types";
import { query } from "gql-query-builder";
import type IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";

import { APP_NAME, optionFields, productFields, productVariantFields, variantFields } from "./fields";
import { instagramMetafield, isOfflineMetafield } from "./metafields/order-metafields";
import {
  additionalDescriptionMetafield,
  availableCountriesMetafield,
  brandNameMetafield,
  brandVariantIdMetafield,
  categoryMetafield,
  colorMetafield,
  discountCodeAvailabilityMetafield,
  dominantColorMetafield,
  dropDateMetafield,
  employeeOnlyMetafield,
  featureMetafield,
  femaleModelMetafield,
  hiddenMetafield,
  hoverImageMetafield,
  images,
  informationMetafield,
  launchesMetafield,
  launchMetafield,
  maleModelMetafield,
  materialsMetafield,
  membershipAutoDiscountMetafield,
  membershipMetafield,
  modelInfoMetafield,
  modelInformationMetafield,
  modelMetafield,
  noticeMetafield,
  offlineOnlyMessageMetafield,
  preorderMetafield,
  productVariantMetafield,
  quantityLimitMetafield,
  quantityLimitProductMetafield,
  recommendedStyleMetafield,
  relatedCollectionMetafield,
  releaseDateMetafield,
  seungsuStockMetafield,
  sizeChartMetafield,
  sizeFitMetafield,
  styleWithProductsMetafield,
  tagMetafield,
  thumbnailImagesMetafield,
  unavailableCountriesMetafield,
  warningMetafield,
} from "./metafields/product-metafields";
import {
  mediaImageFragment,
  productVariantFragment,
  seongsuVariantStockMetafield,
  variantMetafields,
  videoImageFragment,
  yeonhuiVariantStockMetafield,
} from "./fragments";
import { relatedProductsMetafield } from "./metafields/blog-metafields";

export const customerProductMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [
    sizeChartMetafield,
    categoryMetafield,
    noticeMetafield,
    dropDateMetafield,
    isOfflineMetafield,
    instagramMetafield,
    additionalDescriptionMetafield,
    quantityLimitProductMetafield,
    launchMetafield,
    seungsuStockMetafield,
    hiddenMetafield,
    preorderMetafield,
    offlineOnlyMessageMetafield,
  ],
  yeseyesee: [
    sizeChartMetafield,
    tagMetafield,
    additionalDescriptionMetafield,
    noticeMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    hiddenMetafield,
    releaseDateMetafield,
    hoverImageMetafield,
  ],
  thisisneverthat: [
    noticeMetafield,
    sizeFitMetafield,
    hiddenMetafield,
    relatedCollectionMetafield,
    maleModelMetafield,
    femaleModelMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    releaseDateMetafield,
  ],
  khakis: [hiddenMetafield, quantityLimitMetafield, quantityLimitProductMetafield, launchesMetafield],
  wellbeingexpress: [
    hiddenMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    featureMetafield,
    brandNameMetafield,
    thumbnailImagesMetafield,
    sizeChartMetafield,
  ],
  sunlove: [hiddenMetafield, quantityLimitMetafield, quantityLimitProductMetafield, tagMetafield],
};

export const productMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [
    sizeChartMetafield,
    categoryMetafield,
    noticeMetafield,
    dropDateMetafield,
    isOfflineMetafield,
    instagramMetafield,
    additionalDescriptionMetafield,
    quantityLimitProductMetafield,
    launchMetafield,
    seungsuStockMetafield,
    hiddenMetafield,
    preorderMetafield,
    membershipMetafield,
    colorMetafield,
    membershipAutoDiscountMetafield,
    offlineOnlyMessageMetafield,
  ],
  yeseyesee: [
    sizeChartMetafield,
    tagMetafield,
    additionalDescriptionMetafield,
    noticeMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    hiddenMetafield,
    releaseDateMetafield,
    hoverImageMetafield,
  ],
  thisisneverthat: [
    noticeMetafield,
    sizeFitMetafield,
    hiddenMetafield,
    relatedCollectionMetafield,
    maleModelMetafield,
    femaleModelMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    availableCountriesMetafield,
    unavailableCountriesMetafield,
    releaseDateMetafield,
    employeeOnlyMetafield,
  ],
  khakis: [
    sizeChartMetafield,
    recommendedStyleMetafield,
    modelMetafield,
    relatedCollectionMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    hiddenMetafield,
    tagMetafield,
    warningMetafield,
    noticeMetafield,
    informationMetafield,
    unavailableCountriesMetafield,
    launchesMetafield,
    discountCodeAvailabilityMetafield,
    membershipAutoDiscountMetafield,
  ],
  sunlove: [
    sizeChartMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    featureMetafield,
    materialsMetafield,
    tagMetafield,
    relatedProductsMetafield,
    dominantColorMetafield,
    hiddenMetafield,
    hoverImageMetafield,
    modelInformationMetafield,
    releaseDateMetafield,
    noticeMetafield,
  ],
  wellbeingexpress: [
    noticeMetafield,
    sizeChartMetafield,
    modelInfoMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    tagMetafield,
    relatedProductsMetafield,
    styleWithProductsMetafield,
    hiddenMetafield,
    materialsMetafield,
    thumbnailImagesMetafield,
  ],
};

export const variants = (first: number): object => {
  return {
    operation: `variants(first: ${first})`,
    fields: [
      {
        edges: [
          {
            node: [
              ...productVariantFields,
              productVariantMetafield,
              brandVariantIdMetafield,
              ...variantMetafields[APP_NAME],
            ],
          },
        ],
      },
    ],
    variables: {},
  };
};

export interface IProductMetafields {
  launch?: Metafield;
  category?: Metafield;
  sizeChart?: Metafield;
  notice?: Metafield;
  isOffline?: Metafield;
  instagram?: Metafield;
  additionalDescription?: Metafield;
  quantityLimit?: Metafield;
  quantityLimitProduct?: Metafield;
  tag?: Metafield;
  hidden?: Metafield;
  employeeOnly?: Metafield;
  seoungsu_stock?: Metafield;
  preorder?: Metafield;
  size_fit?: Metafield;
  related_collection?: Metafield;
  maleModel?: Metafield;
  femaleModel?: Metafield;
  availableCountries?: Metafield;
  unavailableCountries?: Metafield;
  release_date?: Metafield;
  recommended_style?: Metafield;
  model?: Metafield;
  accessible_memberships?: Metafield;
  warning?: Metafield;
  information?: Metafield;
  sale?: Metafield;
  brandName?: Metafield;
  feature?: Metafield;
  materials?: Metafield;
  relatedProducts?: Metafield;
  dominantColor?: Metafield;
  hoverImage?: Metafield;
  modelInformation?: Metafield;
  styleWithProducts?: Metafield;
  thumbnailImages?: Metafield;
  launches?: Metafield;
  discountCodeAvailability?: Metafield;
  membershipAutoDiscount?: Metafield;
  color?: Metafield;
  dropDate?: Metafield;
  offlineOnlyMessage?: Metafield;
}

export interface IProductVariantMetafields {
  brandVariantId?: Metafield;
}

export const commonProductFields = [
  ...productFields,
  ...productMetafields[APP_NAME],
  {
    operation: `collections(first: 20)`,
    fields: [{ edges: [{ node: ["title", "handle", "description"] }] }],
    variables: {},
  },
  variants(60),
];

// 컬렉션용 상품 메타필드 (경량용)
export const collectionProductMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [hiddenMetafield, launchMetafield, membershipMetafield],
  yeseyesee: [hiddenMetafield, tagMetafield, releaseDateMetafield, hoverImageMetafield],
  thisisneverthat: [hiddenMetafield, releaseDateMetafield],
  khakis: [hiddenMetafield, tagMetafield],
  sunlove: [hiddenMetafield, tagMetafield, hoverImageMetafield, releaseDateMetafield],
  wellbeingexpress: [hiddenMetafield, tagMetafield, thumbnailImagesMetafield],
};

export const commonCollectionProductFields = [
  ...productFields,
  ...productMetafields[APP_NAME],
  ...collectionProductMetafields[APP_NAME],
  {
    operation: `variants(first: 100)`,
    fields: [{ edges: [{ node: [...productVariantFields, ...variantMetafields[APP_NAME]] }] }],
    variables: {},
  },
];

export const commonCustomerProductFields = [...productFields, ...customerProductMetafields[APP_NAME], variants(40)];

export interface IProductDetailNode extends ProductVariant {
  restock?: Metafield;
  seongsuStock?: Metafield;
  yeonhuiStock?: Metafield;
  brandName?: Metafield;
}

/**
 * 상품 상세 조회
 */
export interface IProductDetailVariants extends ProductVariantConnection {
  edges: (ProductVariantEdge & { node: IProductDetailNode })[];
}

export interface IProductDetailQuery {
  product?: Product &
    IProductMetafields & {
      variants: IProductDetailVariants;
      hidden?: Metafield;
      brandName?: Metafield;
      launches?: Metafield;
    };
}

export const productDetailQuery = query(
  {
    operation: "product",
    fields: [
      ...commonProductFields,
      images,
      {
        operation: "media(first: 50)",
        fields: [{ edges: [{ node: ["alt", "id", "mediaContentType", mediaImageFragment, videoImageFragment] }] }],
        variables: {},
      },
      { options: optionFields },
    ],
    variables: { handle: { type: "String!" } },
  },
  undefined,
  { operationName: "ProductDetail" },
);

/**
 * 상품 상세 추천 상품 조회
 */
export interface IProductDetailRecommendQuery {
  productRecommendations: IProductDetailQuery["product"][];
}

export const productDetailRecommendQuery = query(
  {
    operation: "productRecommendations",
    fields: [...commonProductFields, images],
    variables: {
      productId: { type: "ID!" },
      intent: { type: "ProductRecommendationIntent" },
    },
  },
  undefined,
  { operationName: "ProductRecommendations" },
);

/**
 * 상품 variant 조회
 */
export interface IProductVariantQuery {
  node: ProductVariant & {
    product: Product & IProductMetafields;
  };
}

export const productVariantQuery = query(
  {
    operation: "node",
    fields: [
      {
        ...productVariantFragment,
        fields: [
          ...variantFields,
          { product: [...productFields, launchMetafield] },
          dropDateMetafield,
          launchMetafield,
          seongsuVariantStockMetafield,
          yeonhuiVariantStockMetafield,
        ],
      },
    ],
    variables: { id: { type: "ID!" } },
  },
  undefined,
  { operationName: "ProductVariant" },
);

export interface IProductsQuery {
  products: { edges: { node: Product & IProductMetafields }[] };
}

export const productsQuery = query({
  operation: "products",
  fields: [
    {
      edges: [
        {
          node: [
            ...productFields,
            thumbnailImagesMetafield,
            relatedProductsMetafield,
            dropDateMetafield,
            launchMetafield,
          ],
        },
      ],
    },
  ],
  variables: {
    first: { type: "Int!" },
    query: { type: "String" },
    reverse: { type: "Boolean" },
    sortKey: { type: "ProductSortKeys" },
  },
});
